/**
 * Renaming a strategy where you use it — ROADMAP 5.28.
 *
 * The name is the trader's: "Monday condor", "the one that works on
 * Thursdays". Nothing in the product trusts it — every place that needs to
 * know what a position IS derives that from the legs (`structureNameOf`) —
 * which is why it can be handed over at all.
 *
 * EDITED IN PLACE, in the confirmation and on the strategy page, because a
 * name is thought of while looking at the thing it names.
 *
 * THE STORED NAME IS WHAT IS EDITED, not the one on screen. The header
 * shows a Short Straddle bought as "Long Straddle" (`directedName`); if
 * editing started from that, one click of the pencil would silently
 * rename the saved strategy to its own mirror image.
 */

import { useEffect, useRef, useState } from 'react'
import { Check, Pencil, X } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Matches the server's bound, so the input cannot compose a refusal. */
export const STRATEGY_NAME_MAX = 60

export function StrategyName({
  /** What is displayed when not editing — may be the directed form. */
  display,
  /** What is EDITED: the stored name. */
  stored,
  onRename,
  busy = false,
  className,
  testId = 'strategy-name',
}: {
  display: string
  stored: string
  onRename: (name: string) => void
  busy?: boolean
  className?: string
  testId?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(stored)
  const input = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!editing) return
    setDraft(stored)
    // Select the whole name: renaming usually replaces it rather than
    // appending to it.
    const id = window.requestAnimationFrame(() => input.current?.select())
    return () => window.cancelAnimationFrame(id)
  }, [editing, stored])

  const commit = (): void => {
    const next = draft.trim()
    setEditing(false)
    if (next && next !== stored) onRename(next.slice(0, STRATEGY_NAME_MAX))
  }

  if (!editing) {
    return (
      <span className={cn('group flex min-w-0 items-center gap-1.5', className)}>
        <span className="truncate" data-testid={testId}>
          {display}
        </span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label="Rename this strategy"
          data-testid={`${testId}-edit`}
          className="shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus:opacity-100 group-hover:opacity-100"
        >
          <Pencil size={12} />
        </button>
      </span>
    )
  }

  return (
    <span className={cn('flex min-w-0 items-center gap-1', className)}>
      <input
        ref={input}
        value={draft}
        disabled={busy}
        maxLength={STRATEGY_NAME_MAX}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
          // Escape abandons the edit. A rename is never urgent, and a
          // half-typed name saved by a stray click is a nuisance to undo.
          if (e.key === 'Escape') setEditing(false)
        }}
        aria-label="Strategy name"
        data-testid={`${testId}-input`}
        className="min-w-0 flex-1 rounded border border-primary/60 bg-background px-1.5 py-0.5 text-[15px] font-semibold outline-none"
      />
      <button
        type="button"
        onClick={commit}
        aria-label="Save the name"
        data-testid={`${testId}-save`}
        className="shrink-0 text-success hover:brightness-110"
      >
        <Check size={14} />
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        aria-label="Cancel renaming"
        className="shrink-0 text-muted-foreground hover:text-foreground"
      >
        <X size={14} />
      </button>
    </span>
  )
}
